import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { APIError } from '~/src/utils/error';
import { useCurrentOrgFprint } from '../../user';
import { fieldMappingService } from '../services';
import { Project } from '../../project';
import { keys as projectKeys } from '../../project/keys';
import {
  createOptimisticFieldMappingUpdate,
  rollbackOptimisticUpdate,
} from './useOptimisticFieldMappingUpdate';

type DeleteMappingsBySourceAttributeVariables = {
  projectId: number;
  sourceField: string;
};

type DeleteMappingsBySourceAttributeResponse = {
  deleted_count: number;
};

type DeleteMappingsBySourceAttributeContext = {
  previousProject?: Project;
};

export const useDeleteMappingsBySourceAttribute = (
  options: UseMutationOptions<
    DeleteMappingsBySourceAttributeResponse,
    APIError,
    DeleteMappingsBySourceAttributeVariables,
    DeleteMappingsBySourceAttributeContext
  > = {},
) => {
  const orgFprint = useCurrentOrgFprint();
  const queryClient = useQueryClient();

  return useMutation<
    DeleteMappingsBySourceAttributeResponse,
    APIError,
    DeleteMappingsBySourceAttributeVariables,
    DeleteMappingsBySourceAttributeContext
  >({
    mutationFn: ({ projectId, sourceField }) =>
      fieldMappingService.deleteMappingsBySourceAttribute(
        orgFprint,
        projectId,
        sourceField,
      ),
    onMutate: async ({ projectId, sourceField }) => {
      return createOptimisticFieldMappingUpdate(
        queryClient,
        projectId,
        (field) => {
          const mappedSource = field.field_mapping_metadata?.source_field;
          const isMatch =
            mappedSource &&
            (mappedSource.endsWith(`.${sourceField}`) ||
              mappedSource === sourceField);
          if (isMatch) {
            const { field_mapping_metadata: _, ...fieldWithoutMapping } = field;
            return fieldWithoutMapping;
          }
          return field;
        },
      );
    },
    onError: (err, variables, context) => {
      rollbackOptimisticUpdate(
        queryClient,
        variables.projectId,
        context?.previousProject,
      );
      options.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate project to trigger refetch (useProject uses local state that doesn't react to setQueryData)
      queryClient.invalidateQueries(projectKeys.detail(variables.projectId));
      options.onSuccess?.(data, variables, context);
    },
  });
};
